import { useRef, useState, useEffect, useCallback } from "react";
import { motion, useInView, AnimatePresence } from "framer-motion";

/* ─── Constants ─────────────────────────────────────────────── */
const ORANGE = "#FF4A16";
const ORANGE_GLOW = "rgba(255,74,22,0.35)";
const ORANGE_DIM = "rgba(255,74,22,0.12)";

/* ─── Motion presets ─────────────────────────────────────────── */
const fadeUp = {
  hidden: { opacity: 0, y: 32 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.65, ease: [0.22, 1, 0.36, 1] } },
};
const stagger = {
  hidden: {},
  visible: { transition: { staggerChildren: 0.11, delayChildren: 0.04 } },
};

/* ─── Reveal helpers ─────────────────────────────────────────── */
function Reveal({ children, delay = 0, className = "" }: { children: React.ReactNode; delay?: number; className?: string }) {
  const ref = useRef<HTMLDivElement>(null);
  const inView = useInView(ref, { once: true, margin: "-50px" });
  return (
    <motion.div ref={ref} initial="hidden" animate={inView ? "visible" : "hidden"} variants={fadeUp}
      transition={{ duration: 0.65, ease: [0.22, 1, 0.36, 1], delay }} className={className}>
      {children}
    </motion.div>
  );
}
function RevealGroup({ children, className = "" }: { children: React.ReactNode; className?: string }) {
  const ref = useRef<HTMLDivElement>(null);
  const inView = useInView(ref, { once: true, margin: "-50px" });
  return (
    <motion.div ref={ref} initial="hidden" animate={inView ? "visible" : "hidden"} variants={stagger} className={className}>
      {children}
    </motion.div>
  );
}

/* ─── GlassCard ──────────────────────────────────────────────── */
function GlassCard({ children, className = "", style }: { children: React.ReactNode; className?: string; style?: React.CSSProperties }) {
  return (
    <div className={`rounded-[20px] border p-5 ${className}`}
      style={{ background: "rgba(255,255,255,0.04)", borderColor: "rgba(255,255,255,0.09)", backdropFilter: "blur(12px)", ...style }}>
      {children}
    </div>
  );
}

/* ─── Buttons ────────────────────────────────────────────────── */
function OrangeBtn({ children, onClick, className = "", small = false }: { children: React.ReactNode; onClick?: () => void; className?: string; small?: boolean }) {
  return (
    <motion.button onClick={onClick} whileTap={{ scale: 0.96 }} whileHover={{ scale: 1.02 }}
      className={`w-full ${small ? "py-3 text-sm" : "py-4 text-base"} px-8 rounded-xl font-bold text-white tracking-widest uppercase cursor-pointer select-none ${className}`}
      style={{ background: ORANGE, fontFamily: '"Oswald", sans-serif', boxShadow: `0 0 28px ${ORANGE_GLOW}, 0 4px 18px rgba(255,74,22,0.25)`, letterSpacing: "0.12em" }}>
      {children}
    </motion.button>
  );
}
function GhostBtn({ children, onClick }: { children: React.ReactNode; onClick?: () => void }) {
  return (
    <motion.button onClick={onClick} whileTap={{ scale: 0.96 }} whileHover={{ scale: 1.02 }}
      className="w-full py-4 px-8 rounded-xl font-bold tracking-widest uppercase cursor-pointer select-none"
      style={{ background: "transparent", border: `1.5px solid ${ORANGE}`, color: ORANGE, fontFamily: '"Oswald", sans-serif', letterSpacing: "0.12em", fontSize: "0.9rem" }}>
      {children}
    </motion.button>
  );
}

/* ─── Section helpers ────────────────────────────────────────── */
function SectionTitle({ children }: { children: React.ReactNode }) {
  return (
    <h2 className="text-3xl font-bold uppercase text-white mb-2"
      style={{ fontFamily: '"Oswald", sans-serif', letterSpacing: "0.06em", lineHeight: 1.1 }}>
      {children}
    </h2>
  );
}
function OrangeLine() {
  return <div className="w-12 h-[2px] mt-3 mb-8" style={{ background: ORANGE }} />;
}

/* ─── 24-hour Countdown ──────────────────────────────────────── */
const TIMER_KEY = "labkovsky_timer_start";
const DURATION = 24 * 60 * 60;

function useCountdown() {
  const getRemaining = useCallback(() => {
    if (typeof window === "undefined") return DURATION;
    const stored = localStorage.getItem(TIMER_KEY);
    if (!stored) { localStorage.setItem(TIMER_KEY, String(Date.now())); return DURATION; }
    const elapsed = Math.floor((Date.now() - Number(stored)) / 1000);
    return DURATION - (elapsed % DURATION);
  }, []);
  const [remaining, setRemaining] = useState(DURATION);
  useEffect(() => {
    setRemaining(getRemaining());
    const id = setInterval(() => setRemaining(getRemaining()), 1000);
    return () => clearInterval(id);
  }, [getRemaining]);
  const pad = (n: number) => String(n).padStart(2, "0");
  return { h: pad(Math.floor(remaining / 3600)), m: pad(Math.floor((remaining % 3600) / 60)), s: pad(remaining % 60) };
}

function CountdownTimer({ compact = false }: { compact?: boolean }) {
  const { h, m, s } = useCountdown();
  const numSize = compact ? "1.55rem" : "2rem";
  return (
    <div>
      <div className="mb-3 text-center">
        <p style={{ color: ORANGE, fontSize: "0.82rem", fontFamily: '"Oswald", sans-serif', fontWeight: 700, letterSpacing: "0.04em", lineHeight: 1.4 }}>
          Из 300 мест осталось всего 82.
        </p>
        <p style={{ color: "rgba(255,255,255,0.55)", fontSize: "0.75rem", marginTop: "4px", lineHeight: 1.5 }}>
          Успейте приобрести билет со скидкой 50% до окончания акции.
        </p>
      </div>
      <div className="flex items-end justify-center gap-1.5 px-4 py-3 rounded-2xl"
        style={{ background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.08)" }}>
        {[{ val: h, label: "ЧАСОВ" }, { val: m, label: "МИНУТ" }, { val: s, label: "СЕКУНД" }].map((unit, i) => (
          <div key={unit.label} style={{ display: "flex", alignItems: "flex-end", gap: "1.5px" }}>
            <div style={{ textAlign: "center" }}>
              <motion.span key={unit.val} initial={{ opacity: 0.5, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.2 }}
                style={{ fontFamily: '"Oswald", sans-serif', fontSize: numSize, fontWeight: 700, color: "white", display: "block", minWidth: compact ? "2.1rem" : "2.5rem", textAlign: "center" }}>
                {unit.val}
              </motion.span>
              <span style={{ fontSize: "0.58rem", color: "rgba(255,255,255,0.38)", textTransform: "uppercase", letterSpacing: "0.15em", display: "block", marginTop: "2px" }}>{unit.label}</span>
            </div>
            {i < 2 && <span style={{ fontFamily: '"Oswald", sans-serif', fontSize: compact ? "1.35rem" : "1.65rem", fontWeight: 700, color: ORANGE, lineHeight: 1, paddingBottom: "14px" }}>:</span>}
          </div>
        ))}
      </div>
    </div>
  );
}

/* ─── Popup Form ─────────────────────────────────────────────── */
interface PopupProps { open: boolean; onClose: () => void; tier?: string; }

// Форма заявки Tilda
const AMO_FORM_URL = "https://formanm.tilda.ws/fnm";

function PopupForm({ open, onClose, tier }: PopupProps) {
  // Пересоздаём iframe при каждом открытии — иначе форма кэшируется
  const [iframeKey, setIframeKey] = useState(0);
  useEffect(() => { if (open) setIframeKey((k) => k + 1); }, [open]);

  return (
    <AnimatePresence>
      {open && (
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
          className="fixed inset-0 flex items-end justify-center z-50 px-4 pb-4"
          style={{ background: "rgba(0,0,0,0.82)", backdropFilter: "blur(8px)" }}
          onClick={onClose}>
          <motion.div initial={{ y: 60, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: 60, opacity: 0 }}
            transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
            className="w-full rounded-[24px] relative overflow-hidden"
            style={{ background: "#141414", border: "1px solid rgba(255,255,255,0.1)", maxWidth: "460px", maxHeight: "92vh", boxShadow: `0 0 60px rgba(255,74,22,0.18)` }}
            onClick={(e) => e.stopPropagation()}>

            {/* ── Заголовок попапа ── */}
            <div className="px-6 pt-6 pb-4 relative"
              style={{ borderBottom: "1px solid rgba(255,255,255,0.07)" }}>
              <button onClick={onClose}
                className="absolute top-4 right-4 w-8 h-8 flex items-center justify-center rounded-full cursor-pointer"
                style={{ background: "rgba(255,255,255,0.08)", color: "rgba(255,255,255,0.5)", fontSize: "1rem", border: "none" }}>✕</button>
              <div className="flex items-center gap-2 mb-1">
                <div className="w-4 h-[2px]" style={{ background: ORANGE }} />
                <span className="text-xs font-bold tracking-widest uppercase"
                  style={{ color: ORANGE, fontFamily: '"Oswald", sans-serif' }}>
                  {tier ? `Тариф ${tier}` : "Регистрация"}
                </span>
              </div>
              <h3 className="text-white text-xl font-bold uppercase" style={{ fontFamily: '"Oswald", sans-serif' }}>
                Хочу участвовать
              </h3>
              <p className="text-white/40 text-xs mt-0.5">11 июля 2026 · Алматы · Дворец Республики</p>
            </div>

            {/* ── Форма AmoCRM в iframe ── */}
            <div style={{ background: "#fff", borderRadius: "0 0 24px 24px", overflow: "hidden" }}>
              <iframe
                key={iframeKey}
                src={AMO_FORM_URL}
                width="100%"
                height="420"
                frameBorder="0"
                title="Форма регистрации"
                scrolling="no"
                style={{ display: "block", border: "none" }}
              />
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

/* ─── Hero ───────────────────────────────────────────────────── */
function Hero({ onCTA }: { onCTA: () => void }) {
  return (
    <section id="hero" className="relative min-h-screen flex flex-col justify-end overflow-hidden" style={{ background: "#0A0A0A" }}>
      <div className="absolute pointer-events-none" style={{ right: "-5%", top: "0%", width: "75%", height: "80%", background: "radial-gradient(ellipse 70% 60% at 60% 35%, rgba(255,140,30,0.22) 0%, rgba(255,74,22,0.14) 35%, transparent 70%)", filter: "blur(24px)", zIndex: 1 }} />
      <div className="absolute pointer-events-none" style={{ right: "5%", top: "5%", width: "55%", height: "55%", background: "radial-gradient(ellipse at 50% 30%, rgba(255,200,80,0.08) 0%, transparent 65%)", filter: "blur(18px)", zIndex: 1 }} />
      <div className="absolute" style={{ right: "-4%", bottom: "160px", width: "58%", height: "55%", zIndex: 2, maskImage: "linear-gradient(to bottom, transparent 0%, black 18%, black 65%, transparent 100%)", WebkitMaskImage: "linear-gradient(to bottom, transparent 0%, black 18%, black 65%, transparent 100%)" }}>
        <img src="/images/labkovsky-new-cutout.png" alt="" style={{ width: "100%", height: "100%", objectFit: "contain", objectPosition: "bottom right", filter: "drop-shadow(0 0 48px rgba(255,120,30,0.28)) drop-shadow(0 8px 32px rgba(0,0,0,0.7))" }} />
      </div>
      <div className="absolute inset-0" style={{ background: "linear-gradient(to right, rgba(10,10,10,0.92) 28%, rgba(10,10,10,0.5) 55%, transparent 100%)", zIndex: 3 }} />
      <div className="absolute inset-0" style={{ background: "linear-gradient(to bottom, transparent 35%, rgba(10,10,10,0.75) 65%, #0A0A0A 88%)", zIndex: 3 }} />

      <div className="relative px-5 pb-10 pt-20" style={{ zIndex: 4 }}>
        <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.2 }} className="mb-3">
          <span className="text-xs font-bold tracking-[0.28em] uppercase text-white/55" style={{ fontFamily: '"Oswald", sans-serif' }}>МИХАИЛ ЛАБКОВСКИЙ</span>
        </motion.div>
        <motion.div initial={{ opacity: 0, x: -18 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.6, delay: 0.35 }} className="inline-flex items-center gap-2 mb-5">
          <div className="w-5 h-[2px]" style={{ background: ORANGE }} />
          <span className="text-sm font-bold uppercase tracking-[0.18em]" style={{ color: ORANGE, fontFamily: '"Oswald", sans-serif' }}>ЖИВАЯ ВСТРЕЧА</span>
        </motion.div>
        <motion.h1 initial={{ opacity: 0, y: 22 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.45, ease: [0.22, 1, 0.36, 1] }}
          className="text-white font-bold uppercase mb-4 leading-[1.0]"
          style={{ fontFamily: '"Oswald", sans-serif', fontSize: "clamp(2rem, 7.5vw, 3rem)", letterSpacing: "0.02em" }}>
          <span style={{ color: ORANGE }}>"</span>КАК ДЫШАТЬ<br />ПОЛНОЙ ГРУДЬЮ<br />В ПОЛНОЙ Ж*ПЕ<span style={{ color: ORANGE }}>"</span>
        </motion.h1>

        {/* Speaker photo under title — cutout + neon glow */}
        <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.75, delay: 0.52, ease: [0.22, 1, 0.36, 1] }}
          className="-mx-5 mb-4 flex justify-center relative" style={{ height: "300px" }}>
          <div className="absolute" style={{ bottom: 0, left: "50%", transform: "translateX(-50%)", width: "90%", height: "95%", zIndex: 1, background: "radial-gradient(ellipse 70% 60% at 50% 80%, #FF6000 0%, #FF4A16 22%, rgba(255,74,22,0.45) 48%, rgba(255,30,0,0.12) 65%, transparent 78%)", filter: "blur(18px)" }} />
          <div className="absolute" style={{ bottom: "10%", left: "50%", transform: "translateX(-50%)", width: "110%", height: "60%", zIndex: 1, background: "radial-gradient(ellipse at 50% 90%, rgba(255,100,0,0.25) 0%, transparent 65%)", filter: "blur(28px)" }} />
          <img src="/images/labkovsky-photo-cut.png" alt="Михаил Лабковский" style={{ position: "relative", zIndex: 2, height: "100%", width: "auto", objectFit: "contain", objectPosition: "bottom center", filter: "drop-shadow(0 0 40px rgba(255,74,22,0.55)) drop-shadow(0 4px 24px rgba(0,0,0,0.8))" }} />
          <div className="absolute inset-x-0 bottom-0 h-16" style={{ background: "linear-gradient(to top, #0A0A0A 0%, transparent 100%)", zIndex: 3 }} />
        </motion.div>

        <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.7, delay: 0.58 }}
          className="text-white/80 text-sm leading-relaxed mb-6 text-center px-4 py-3 rounded-2xl"
          style={{ border: "1px solid rgba(255,74,22,0.3)", background: "rgba(255,74,22,0.05)", backdropFilter: "blur(8px)" }}>
          Откровенный разговор о том, как сохранять себя, внутреннюю опору и спокойствие в сложные времена.
        </motion.p>
        <motion.div initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.68 }} className="grid grid-cols-2 gap-2 mb-6">
          {[{ icon: "📅", text: "11 июля 2026" }, { icon: "📍", text: "Дворец Республики" }, { icon: "🎤", text: "Живая встреча" }, { icon: "🕙", text: "16:00 – 18:00" }].map((item) => (
            <div key={item.text} className="flex items-center gap-2 px-3 py-2.5 rounded-xl" style={{ background: "rgba(255,255,255,0.06)", border: "1px solid rgba(255,255,255,0.09)" }}>
              <span className="text-xs">{item.icon}</span>
              <span className="text-white/75 text-xs font-medium">{item.text}</span>
            </div>
          ))}
        </motion.div>
        <motion.div initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.8 }}>
          <OrangeBtn onClick={onCTA}>ХОЧУ УЧАСТВОВАТЬ</OrangeBtn>
          <p className="text-center text-white/30 text-xs mt-2.5 tracking-wider uppercase" style={{ fontFamily: '"Oswald", sans-serif' }}>Количество мест ограничено</p>
        </motion.div>
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.6, delay: 1.0 }} className="mt-5">
          <CountdownTimer />
        </motion.div>
      </div>
    </section>
  );
}

/* ─── Who (swiper) ───────────────────────────────────────────── */
const WHO_CARDS = [
  { icon: "😮‍💨", title: "Тем, кто устал быть сильным", text: "Когда снаружи вы справляетесь со всем, а внутри живёте с тревогой, напряжением и усталостью." },
  { icon: "😰", title: "Тем, кто постоянно тревожится", text: "Вы поймёте, почему психика всё время в режиме напряжения, и узнаете, как вернуть внутреннее спокойствие." },
  { icon: "💔", title: "Тем, кто теряет себя в отношениях", text: "Если вы часто терпите, подстраиваетесь под других, боитесь обидеть или чувствуете себя виноватым." },
  { icon: "⛓️", title: "Тем, кто живёт через «надо»", text: "Когда обязанности вытеснили желания, а радость и ощущение свободы остались где-то в прошлом." },
  { icon: "🌀", title: "Тем, кто переживает внутренний кризис", text: "Если вы не понимаете, куда двигаться дальше, чего хотите на самом деле и как изменить свою жизнь." },
  { icon: "🙋", title: "Тем, кто хочет задать личный вопрос", text: "Живая встреча даст возможность задать свой вопрос Михаилу Лабковскому и получить разбор конкретной ситуации." },
];

function WhoSection() {
  const [active, setActive] = useState(0);
  const scrollRef = useRef<HTMLDivElement>(null);

  function scrollTo(idx: number) {
    const card = scrollRef.current?.children[idx] as HTMLElement;
    card?.scrollIntoView({ behavior: "smooth", block: "nearest", inline: "center" });
    setActive(idx);
  }
  function handleScroll() {
    if (!scrollRef.current) return;
    const idx = Math.round(scrollRef.current.scrollLeft / (scrollRef.current.scrollWidth / WHO_CARDS.length));
    setActive(Math.max(0, Math.min(idx, WHO_CARDS.length - 1)));
  }

  return (
    <section className="py-16 overflow-hidden" style={{ background: "#0A0A0A" }}>
      <Reveal className="px-5">
        <SectionTitle>КОМУ ПОДОЙДЁТ<br />МАСТЕР-КЛАСС</SectionTitle>
        <OrangeLine />
      </Reveal>

      {/* Swiper */}
      <div ref={scrollRef} onScroll={handleScroll}
        className="flex gap-4 overflow-x-auto pb-3 px-5"
        style={{ scrollSnapType: "x mandatory", scrollbarWidth: "none", WebkitOverflowScrolling: "touch" }}>
        {WHO_CARDS.map((card, i) => (
          <motion.div key={i} onClick={() => scrollTo(i)}
            animate={{ scale: active === i ? 1 : 0.94, opacity: active === i ? 1 : 0.55 }}
            transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
            className="flex-shrink-0 cursor-pointer"
            style={{ width: "72vw", maxWidth: "280px", scrollSnapAlign: "center" }}>
            <div className="h-full rounded-[20px] p-5 flex flex-col gap-3"
              style={{
                background: active === i ? "rgba(255,74,22,0.07)" : "rgba(255,255,255,0.04)",
                border: active === i ? `1.5px solid rgba(255,74,22,0.35)` : "1px solid rgba(255,255,255,0.08)",
                boxShadow: active === i ? "0 0 24px rgba(255,74,22,0.12)" : "none",
                transition: "all 0.3s ease",
                minHeight: "190px",
              }}>
              <div className="w-10 h-10 rounded-xl flex items-center justify-center text-xl flex-shrink-0"
                style={{ background: ORANGE_DIM, border: "1px solid rgba(255,74,22,0.25)" }}>
                {card.icon}
              </div>
              <div>
                <p className="text-white font-bold text-sm leading-snug mb-2"
                  style={{ fontFamily: '"Oswald", sans-serif', letterSpacing: "0.02em" }}>
                  {card.title}
                </p>
                <p className="text-white/55 text-xs leading-relaxed">{card.text}</p>
              </div>
            </div>
          </motion.div>
        ))}
      </div>

      {/* Dots */}
      <div className="flex justify-center gap-2 mt-4 px-5">
        {WHO_CARDS.map((_, i) => (
          <button key={i} onClick={() => scrollTo(i)} className="rounded-full transition-all duration-300 cursor-pointer"
            style={{ width: active === i ? "22px" : "7px", height: "7px", background: active === i ? ORANGE : "rgba(255,255,255,0.2)" }} />
        ))}
      </div>

      {/* Counter */}
      <p className="text-center mt-3 text-white/25 text-xs" style={{ fontFamily: '"Oswald", sans-serif' }}>
        {active + 1} / {WHO_CARDS.length}
      </p>
    </section>
  );
}

/* ─── Topics ─────────────────────────────────────────────────── */
function TopicsSection() {
  const items = ["Почему нам бывает так тяжело внутри", "Как перестать жить в постоянном напряжении", "Как не терять себя в хаосе жизни", "Как научиться выбирать себя без чувства вины", "Как вернуть ощущение свободы и внутреннего спокойствия"];
  return (
    <section className="px-5 py-16" style={{ background: "#0D0D0D" }}>
      <Reveal><SectionTitle>О ЧЁМ ПОГОВОРИМ</SectionTitle><OrangeLine /></Reveal>
      <RevealGroup className="flex flex-col gap-3">
        {items.map((item, i) => (
          <motion.div key={i} variants={fadeUp}>
            <div className="flex items-start gap-4 px-5 py-4 rounded-[18px]" style={{ background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.08)" }}>
              <span className="text-lg font-bold flex-shrink-0 mt-0.5" style={{ color: ORANGE, fontFamily: '"Oswald", sans-serif', minWidth: "1.6rem" }}>{String(i + 1).padStart(2, "0")}</span>
              <p className="text-white/85 text-sm leading-relaxed">{item}</p>
            </div>
          </motion.div>
        ))}
      </RevealGroup>
    </section>
  );
}

/* ─── Pricing ────────────────────────────────────────────────── */
function PricingSection({ onPopup }: { onPopup: (tier: string) => void }) {
  const silverFeatures = ["Участие на мастер-классе", "Места категории C и D", "Сессия вопросов и ответов со спикером", "Автограф-сессия"];
  const goldFeatures = ["Участие на мастер-классе", "VIP-места (первые ряды)", "Сессия вопросов и ответов со спикером", "Автограф-сессия", "Книга «Хочу и буду» с личным автографом"];

  return (
    <section className="px-5 py-16" style={{ background: "#0A0A0A" }}>
      <Reveal><SectionTitle>ТАРИФЫ УЧАСТИЯ</SectionTitle><OrangeLine /></Reveal>
      <Reveal delay={0.05} className="mb-8"><CountdownTimer /></Reveal>

      <RevealGroup className="flex flex-col gap-5">

        {/* ── Silver ── */}
        <motion.div variants={fadeUp}>
          <div className="relative">
            {/* Скидка-бейдж Silver */}
            <div className="absolute -top-3 right-4 z-10">
              <span className="px-3 py-1 rounded-full text-white text-xs font-bold" style={{ background: "#C0392B", fontFamily: '"Oswald", sans-serif', fontSize: "0.7rem", letterSpacing: "0.08em", boxShadow: "0 4px 14px rgba(192,57,43,0.5)" }}>
                СКИДКА −50%
              </span>
            </div>
            <GlassCard style={{ border: "1px solid rgba(192,192,192,0.2)" }}>
              {/* Header */}
              <div className="flex items-start justify-between mb-4">
                <div>
                  <h3 className="text-white font-bold text-xl uppercase" style={{ fontFamily: '"Oswald", sans-serif', letterSpacing: "0.08em" }}>Silver</h3>
                  <p className="text-white/40 text-xs mt-0.5">Стандартное участие</p>
                </div>
                <div className="text-right">
                  <p className="text-white font-bold text-2xl leading-none" style={{ fontFamily: '"Oswald", sans-serif' }}>40 000 ₸</p>
                  <p className="text-white/35 text-sm line-through mt-0.5">80 000 ₸</p>
                </div>
              </div>
              <div className="w-full h-[1px] mb-4" style={{ background: "rgba(255,255,255,0.07)" }} />
              {/* Features */}
              <div className="flex flex-col gap-2.5 mb-5">
                {silverFeatures.map((f) => (
                  <div key={f} className="flex items-start gap-2.5">
                    <span className="text-sm flex-shrink-0 mt-0.5" style={{ color: ORANGE }}>✔</span>
                    <span className="text-white/75 text-sm">{f}</span>
                  </div>
                ))}
              </div>
              <GhostBtn onClick={() => onPopup("Silver")}>Хочу участвовать</GhostBtn>
            </GlassCard>
          </div>
        </motion.div>

        {/* ── Gold ── */}
        <motion.div variants={fadeUp}>
          <div className="relative">
            {/* Популярный бейдж */}
            <div className="absolute -top-3 left-1/2 -translate-x-1/2 z-10">
              <span className="px-3 py-1 rounded-full text-white text-xs font-bold whitespace-nowrap" style={{ background: ORANGE, fontFamily: '"Oswald", sans-serif', fontSize: "0.68rem", letterSpacing: "0.1em", boxShadow: `0 4px 16px ${ORANGE_GLOW}` }}>
                САМЫЙ ПОПУЛЯРНЫЙ ВЫБОР
              </span>
            </div>
            {/* Скидка-бейдж Gold */}
            <div className="absolute -top-3 right-4 z-10">
              <span className="px-3 py-1 rounded-full text-white text-xs font-bold" style={{ background: "#C0392B", fontFamily: '"Oswald", sans-serif', fontSize: "0.7rem", letterSpacing: "0.08em", boxShadow: "0 4px 14px rgba(192,57,43,0.5)" }}>
                СКИДКА −50%
              </span>
            </div>
            <GlassCard style={{ border: `1.5px solid ${ORANGE}`, boxShadow: `0 0 36px rgba(255,74,22,0.2), inset 0 0 32px rgba(255,74,22,0.03)` }}>
              {/* Header */}
              <div className="flex items-start justify-between mb-4">
                <div>
                  <h3 className="font-bold text-xl uppercase" style={{ fontFamily: '"Oswald", sans-serif', letterSpacing: "0.08em", color: ORANGE }}>Gold</h3>
                  <p className="text-white/40 text-xs mt-0.5">VIP участие</p>
                </div>
                <div className="text-right">
                  <p className="font-bold text-2xl leading-none" style={{ fontFamily: '"Oswald", sans-serif', color: ORANGE }}>60 000 ₸</p>
                  <p className="text-white/35 text-sm line-through mt-0.5">120 000 ₸</p>
                </div>
              </div>
              <div className="w-full h-[1px] mb-4" style={{ background: `rgba(255,74,22,0.2)` }} />
              {/* Features */}
              <div className="flex flex-col gap-2.5 mb-5">
                {goldFeatures.map((f, i) => (
                  <div key={f} className="flex items-start gap-2.5">
                    <span className="text-sm flex-shrink-0 mt-0.5" style={{ color: ORANGE }}>✔</span>
                    <span className={`text-sm ${i === goldFeatures.length - 1 ? "text-white font-semibold" : "text-white/80"}`}>{f}</span>
                  </div>
                ))}
              </div>

              {/* Book promo block */}
              <div className="flex items-center gap-4 p-3 rounded-2xl mb-5" style={{ background: "rgba(255,74,22,0.07)", border: "1px solid rgba(255,74,22,0.2)" }}>
                <img src="/images/book-hochu.webp" alt="Хочу и буду" className="rounded-xl flex-shrink-0" style={{ width: "58px", height: "80px", objectFit: "cover", boxShadow: "0 4px 20px rgba(0,0,0,0.5)" }} />
                <div>
                  <p className="text-white/45 text-xs uppercase tracking-widest mb-1" style={{ fontFamily: '"Oswald", sans-serif' }}>Подарок</p>
                  <p className="text-white font-semibold text-sm leading-tight">«Хочу и буду»</p>
                  <p className="text-white/50 text-xs mt-0.5 leading-snug">с личным автографом<br />Михаила Лабковского</p>
                </div>
                <div className="ml-auto flex-shrink-0">
                  <div className="w-6 h-6 rounded-full flex items-center justify-center text-sm" style={{ background: ORANGE_DIM, border: `1px solid rgba(255,74,22,0.4)` }}>🎁</div>
                </div>
              </div>
              <OrangeBtn onClick={() => onPopup("Gold")}>Хочу участвовать</OrangeBtn>
            </GlassCard>
          </div>
        </motion.div>
      </RevealGroup>
      <Reveal delay={0.1} className="mt-5">
        <p className="text-center text-white/25 text-xs tracking-wider uppercase" style={{ fontFamily: '"Oswald", sans-serif' }}>Количество мест строго ограничено</p>
      </Reveal>
    </section>
  );
}

/* ─── Speaker ────────────────────────────────────────────────── */
function SpeakerSection() {
  return (
    <section className="px-5 py-16" style={{ background: "#0D0D0D" }}>
      <Reveal><SectionTitle>О СПИКЕРЕ</SectionTitle><OrangeLine /></Reveal>
      <Reveal>
        <div className="rounded-[22px] overflow-hidden" style={{ border: "1px solid rgba(255,74,22,0.2)", boxShadow: "0 0 40px rgba(255,74,22,0.07)" }}>
          <div className="relative h-72 overflow-hidden">
            <img src="/images/labkovsky-photo-cut.png" alt="Михаил Лабковский" className="w-full h-full object-cover object-top" style={{ filter: "brightness(0.9) contrast(1.04)", background: "#111" }} />
            <div className="absolute inset-0" style={{ background: "linear-gradient(to bottom, transparent 40%, #0D0D0D 100%)" }} />
            <div className="absolute bottom-4 left-5">
              <p className="text-white font-bold text-2xl uppercase leading-tight" style={{ fontFamily: '"Oswald", sans-serif', textShadow: "0 2px 20px rgba(0,0,0,0.8)" }}>МИХАИЛ<br />ЛАБКОВСКИЙ</p>
              <div className="w-10 h-[2px] mt-2" style={{ background: ORANGE }} />
            </div>
          </div>
          <div className="p-5" style={{ background: "rgba(255,255,255,0.03)" }}>
            <p className="text-white/72 text-sm leading-relaxed">Михаил Лабковский — психолог, писатель, юрист, телеведущий и радиоведущий, один из самых известных психологов.</p>
            <p className="text-white/72 text-sm leading-relaxed mt-3">Автор популярной методики, основанной на <span className="text-white font-medium">шести правилах счастливой жизни</span>, которые помогли миллионам людей выстроить здоровые отношения, повысить самооценку и научиться жить в согласии с собой.</p>
            <p className="text-white/72 text-sm leading-relaxed mt-3">Автор бестселлеров <span className="text-white font-medium">«Хочу и буду»</span> и других книг по психологии личности и отношений. Его лекции, выступления и публикации собирают многомиллионную аудиторию по всему миру.</p>
            <p className="text-white/72 text-sm leading-relaxed mt-3">Журнал <span className="text-white font-medium">Forbes</span> неоднократно включал Михаила Лабковского в число самых узнаваемых психологов СНГ.</p>
            <div className="flex flex-wrap gap-2 mt-5">
              {["Психолог", "Forbes СНГ", "Автор бестселлеров", "6 правил счастья"].map((tag) => (
                <span key={tag} className="text-xs px-3 py-1.5 rounded-full font-medium" style={{ background: ORANGE_DIM, color: ORANGE, border: "1px solid rgba(255,74,22,0.25)" }}>{tag}</span>
              ))}
            </div>
          </div>
        </div>
      </Reveal>
    </section>
  );
}

/* ─── Books ──────────────────────────────────────────────────── */
const BOOKS = [
  { img: "/images/book-hochu.webp", title: "Хочу и буду", desc: "Принять себя, полюбить жизнь и стать счастливым" },
  { img: "/images/book-lyublyu.webp", title: "Люблю и понимаю", desc: "Как растить детей счастливыми и любимыми" },
  { img: "/images/book-sdvg.jpg", title: "Моя жизнь с СДВГ", desc: "Практическое руководство для детей и их родителей" },
  { img: "/images/book-privet.webp", title: "Привет из детства", desc: "Вернуться в прошлое, чтобы изменить настоящее" },
];
function BooksSection() {
  const [active, setActive] = useState(0);
  const scrollRef = useRef<HTMLDivElement>(null);
  function scrollTo(idx: number) {
    const card = scrollRef.current?.children[idx] as HTMLElement;
    card?.scrollIntoView({ behavior: "smooth", block: "nearest", inline: "center" });
    setActive(idx);
  }
  function handleScroll() {
    if (!scrollRef.current) return;
    const idx = Math.round(scrollRef.current.scrollLeft / (scrollRef.current.scrollWidth / BOOKS.length));
    setActive(Math.max(0, Math.min(idx, BOOKS.length - 1)));
  }
  return (
    <section className="py-16 overflow-hidden" style={{ background: "#0A0A0A" }}>
      <Reveal className="px-5">
        <SectionTitle>КНИГИ МИХАИЛА<br />ЛАБКОВСКОГО</SectionTitle><OrangeLine />
        <p className="text-white/45 text-sm leading-relaxed mb-8 -mt-4">Бестселлеры, которые помогли миллионам людей изменить отношение к себе и своей жизни.</p>
      </Reveal>
      <div ref={scrollRef} onScroll={handleScroll} className="flex gap-4 overflow-x-auto pb-4 px-5" style={{ scrollSnapType: "x mandatory", scrollbarWidth: "none", WebkitOverflowScrolling: "touch" }}>
        {BOOKS.map((book, i) => (
          <motion.div key={book.title} onClick={() => scrollTo(i)} animate={{ scale: active === i ? 1 : 0.93, opacity: active === i ? 1 : 0.65 }} transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }} className="flex-shrink-0 cursor-pointer" style={{ width: "68vw", maxWidth: "260px", scrollSnapAlign: "center" }}>
            <div className="rounded-[20px] overflow-hidden" style={{ border: active === i ? `1.5px solid ${ORANGE}` : "1px solid rgba(255,255,255,0.08)", boxShadow: active === i ? `0 0 30px rgba(255,74,22,0.2)` : "none", transition: "all 0.3s" }}>
              <div style={{ aspectRatio: "3/4", background: "#111", overflow: "hidden" }}>
                <img src={book.img} alt={book.title} className="w-full h-full object-cover" style={{ transition: "transform 0.4s ease", transform: active === i ? "scale(1.04)" : "scale(1)" }} />
              </div>
              <div className="p-4" style={{ background: "rgba(255,255,255,0.04)" }}>
                <h4 className="text-white font-bold text-base mb-1.5" style={{ fontFamily: '"Oswald", sans-serif', letterSpacing: "0.04em" }}>{book.title}</h4>
                <p className="text-white/50 text-xs leading-relaxed">{book.desc}</p>
              </div>
            </div>
          </motion.div>
        ))}
      </div>
      <div className="flex justify-center gap-2 mt-5">
        {BOOKS.map((_, i) => (
          <button key={i} onClick={() => scrollTo(i)} className="rounded-full transition-all duration-300 cursor-pointer" style={{ width: active === i ? "24px" : "7px", height: "7px", background: active === i ? ORANGE : "rgba(255,255,255,0.2)" }} />
        ))}
      </div>
    </section>
  );
}

/* ─── Venue ──────────────────────────────────────────────────── */
function VenueSection() {
  return (
    <section className="px-5 py-16" style={{ background: "#0D0D0D" }}>
      <Reveal>
        <h2 className="text-4xl font-bold text-white uppercase leading-none mb-3" style={{ fontFamily: '"Oswald", sans-serif' }}>11 ИЮЛЯ<br />2026</h2>
        <div className="flex items-center gap-2 mb-8">
          <div className="w-4 h-[2px]" style={{ background: ORANGE }} />
          <span className="text-sm font-medium" style={{ color: ORANGE, fontFamily: '"Oswald", sans-serif' }}>АЛМАТЫ, КАЗАХСТАН</span>
        </div>
      </Reveal>
      <RevealGroup className="flex flex-col gap-3">
        {[{ label: "МЕСТО ПРОВЕДЕНИЯ", value: "Дворец Республики, Алматы" }, { label: "ФОРМАТ", value: "Живая встреча" }, { label: "ВРЕМЯ", value: "16:00 – 18:00" }].map((row) => (
          <motion.div key={row.label} variants={fadeUp}>
            <div className="flex justify-between items-center px-5 py-4 rounded-[18px]" style={{ background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.08)" }}>
              <span className="text-white/40 text-xs tracking-widest uppercase" style={{ fontFamily: '"Oswald", sans-serif' }}>{row.label}</span>
              <span className="text-white font-semibold text-sm">{row.value}</span>
            </div>
          </motion.div>
        ))}
      </RevealGroup>
    </section>
  );
}

/* ─── Registration ───────────────────────────────────────────── */
function RegistrationSection({ onPopup }: { onPopup: () => void }) {
  return (
    <section id="register" className="px-5 py-16 relative overflow-hidden" style={{ background: "#0A0A0A" }}>
      <div className="absolute inset-0 pointer-events-none" style={{ background: "radial-gradient(ellipse at 50% 0%, rgba(255,74,22,0.07) 0%, transparent 65%)" }} />
      <div className="relative z-10">
        <Reveal><SectionTitle>ЗАРЕГИСТРИРУЙТЕСЬ<br />НА МАСТЕР-КЛАСС</SectionTitle><OrangeLine /></Reveal>
        <Reveal delay={0.1} className="flex flex-col gap-4">
          <p className="text-white/55 text-sm leading-relaxed -mt-4">Оставьте заявку — менеджер свяжется с вами и уточнит детали участия.</p>
          <OrangeBtn onClick={onPopup}>ХОЧУ УЧАСТВОВАТЬ</OrangeBtn>
          <p className="text-center text-white/25 text-xs tracking-wider uppercase" style={{ fontFamily: '"Oswald", sans-serif' }}>Количество мест ограничено</p>
        </Reveal>
      </div>
    </section>
  );
}

/* ─── Final ──────────────────────────────────────────────────── */
function FinalSection({ onPopup }: { onPopup: () => void }) {
  return (
    <section className="relative px-5 py-20 text-center overflow-hidden" style={{ background: "#0A0A0A" }}>
      <div className="absolute inset-0 pointer-events-none" style={{ background: "radial-gradient(ellipse at 50% 100%, rgba(255,74,22,0.12) 0%, transparent 60%)" }} />
      <div className="w-full h-[1px] absolute top-0 left-0" style={{ background: "linear-gradient(to right, transparent, rgba(255,74,22,0.35), transparent)" }} />
      <div className="relative z-10">
        <Reveal>
          <div className="flex justify-center mb-6"><div className="w-1 h-12 rounded-full" style={{ background: `linear-gradient(to bottom, ${ORANGE}, transparent)` }} /></div>
          <h2 className="text-white font-bold uppercase mb-6 leading-tight" style={{ fontFamily: '"Oswald", sans-serif', fontSize: "clamp(1.8rem, 7vw, 2.8rem)", letterSpacing: "0.03em" }}>НЕ ОТКЛАДЫВАЙТЕ<br />СЕБЯ НА ПОТОМ</h2>
          <p className="text-white/50 text-sm leading-relaxed mb-8 max-w-xs mx-auto">Эта встреча — возможность остановиться, услышать себя и получить честные ответы на важные вопросы.</p>
          <OrangeBtn onClick={onPopup}>ЗАРЕГИСТРИРОВАТЬСЯ</OrangeBtn>
          <div className="mt-8 flex items-center justify-center gap-3 text-white/20">
            <div className="h-[1px] w-10" style={{ background: "rgba(255,255,255,0.12)" }} />
            <span className="text-xs tracking-widest" style={{ fontFamily: '"Oswald", sans-serif' }}>11 ИЮЛЯ · АЛМАТЫ</span>
            <div className="h-[1px] w-10" style={{ background: "rgba(255,255,255,0.12)" }} />
          </div>
        </Reveal>
      </div>
    </section>
  );
}

/* ─── Footer ─────────────────────────────────────────────────── */
function Footer() {
  return (
    <footer className="px-5 py-8 text-center border-t" style={{ borderColor: "rgba(255,255,255,0.06)", background: "#0A0A0A" }}>
      <p className="text-white/20 text-xs leading-relaxed">© 2026 · Живая встреча с Михаилом Лабковским<br />Алматы, Казахстан · 11 июля 2026</p>
    </footer>
  );
}

/* ─── Page ───────────────────────────────────────────────────── */
const TILDA_FORM = "https://formanm.tilda.ws/fnm";

const Index = () => {
  function openForm() { window.open(TILDA_FORM, "_blank", "noopener,noreferrer"); }

  return (
    <main style={{ background: "#0A0A0A", maxWidth: "480px", margin: "0 auto", minHeight: "100vh" }}>
      <Hero onCTA={openForm} />
      <WhoSection />
      <TopicsSection />
      <PricingSection onPopup={openForm} />
      <SpeakerSection />
      <BooksSection />
      <VenueSection />
      <RegistrationSection onPopup={openForm} />
      <FinalSection onPopup={openForm} />
      <Footer />
    </main>
  );
};

export default Index;
